<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { setColorTheme } from '../../helper/themeHelper';
import {
  ACCESSIBILITY_UI_SETTINGS_KEY,
  LEGACY_ACCESSIBILITY_STORAGE_KEY,
  announceAccessibilityMessage,
  applyAccessibilityPreferences,
  createDefaultAccessibilityPreferences,
  normalizeAccessibilityPreferences,
  preferencesForProfile,
} from './accessibilityPreferences';

const isOpen = ref(false);
const isReady = ref(false);
const dialogRef = ref(null);
const dialogTitleRef = ref(null);
const returnFocusElement = ref(null);
const statusMessage = ref('');
const options = reactive(createDefaultAccessibilityPreferences());

const currentUser = useMapGetter('getCurrentUser');
const { uiSettings, updateUISettings } = useUISettings();

const profiles = [
  {
    id: 'default',
    name: 'Padrão',
    description: 'Preferências visuais padrão da Lumeratec.',
  },
  {
    id: 'lowVision',
    name: 'Baixa visão',
    description: 'Contraste, texto ampliado e foco reforçado.',
  },
  {
    id: 'nonVisual',
    name: 'Navegação não visual',
    description: 'Leitor de tela, briefing e operação por teclado.',
  },
  {
    id: 'keyboard',
    name: 'Operação por teclado',
    description: 'Atalhos, foco reforçado e menos animações.',
  },
];

const colorVisionOptions = [
  { value: 'default', label: 'Cores padrão' },
  { value: 'deuteranopia', label: 'Deuteranopia' },
  { value: 'protanopia', label: 'Protanopia' },
  { value: 'tritanopia', label: 'Tritanopia' },
];

const textScaleLabel = computed(() => `${100 + options.textScale * 10}%`);

const getFocusableElements = () => {
  if (!dialogRef.value) return [];

  return [
    ...dialogRef.value.querySelectorAll(
      'button:not([disabled]), select:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
    ),
  ].filter(element => !element.hasAttribute('hidden'));
};

const closeMenu = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  nextTick(() => returnFocusElement.value?.focus?.());
};

const openMenu = event => {
  returnFocusElement.value =
    event?.detail?.trigger instanceof HTMLElement
      ? event.detail.trigger
      : document.activeElement;
  isOpen.value = true;
  nextTick(() => dialogTitleRef.value?.focus());
};

const toggleMenu = event => {
  if (isOpen.value) closeMenu();
  else openMenu(event);
};

const handleDialogKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeMenu();
    return;
  }

  if (event.key !== 'Tab') return;

  const focusableElements = getFocusableElements();
  if (!focusableElements.length) return;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements.at(-1);

  if (
    event.shiftKey &&
    [firstElement, dialogTitleRef.value].includes(document.activeElement)
  ) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
};

const setProfile = profile => {
  Object.assign(options, preferencesForProfile(profile, options));
  statusMessage.value = `Perfil ${profiles.find(item => item.id === profile)?.name} ativado.`;
};

const markAsCustom = () => {
  options.profile = 'custom';
};

const updateToggle = (key, value) => {
  options[key] = value;
  markAsCustom();
};

const changeTextScale = step => {
  const value = options.textScale + step;
  if (value < 0 || value > 5) return;

  options.textScale = value;
  markAsCustom();
};

const openKeyboardShortcuts = () => {
  closeMenu();
  nextTick(() =>
    window.dispatchEvent(new CustomEvent('open-keyboard-shortcuts'))
  );
};

const resetOptions = () => {
  Object.assign(options, preferencesForProfile('default', options));
  statusMessage.value = 'Preferências de acessibilidade redefinidas.';
  announceAccessibilityMessage(statusMessage.value);
};

const readLegacyPreferences = () => {
  const saved = localStorage.getItem(LEGACY_ACCESSIBILITY_STORAGE_KEY);
  if (!saved) return null;

  try {
    return JSON.parse(saved);
  } catch {
    return null;
  }
};

const loadPreferences = () => {
  const storedPreferences = uiSettings.value?.[ACCESSIBILITY_UI_SETTINGS_KEY];
  const legacyPreferences = readLegacyPreferences();
  const initialProfile =
    currentUser.value?.custom_attributes?.accessibility_profile;

  let preferences = storedPreferences || legacyPreferences;
  if (!preferences && initialProfile) {
    preferences = preferencesForProfile(initialProfile);
  }

  Object.assign(options, normalizeAccessibilityPreferences(preferences));
  setColorTheme(options.darkMode, { force: true });
  applyAccessibilityPreferences(options);
  isReady.value = true;

  if (!storedPreferences && (legacyPreferences || initialProfile)) {
    updateUISettings({
      [ACCESSIBILITY_UI_SETTINGS_KEY]: { ...options },
    });
  }
};

watch(
  options,
  updatedOptions => {
    if (!isReady.value) return;

    setColorTheme(updatedOptions.darkMode, { force: true });
    applyAccessibilityPreferences(updatedOptions);
    updateUISettings({
      [ACCESSIBILITY_UI_SETTINGS_KEY]: { ...updatedOptions },
    });
    localStorage.setItem(
      LEGACY_ACCESSIBILITY_STORAGE_KEY,
      JSON.stringify(updatedOptions)
    );
  },
  { deep: true }
);

const handleGlobalKeydown = event => {
  if (event.altKey && event.shiftKey && event.code === 'KeyA') {
    event.preventDefault();
    toggleMenu(event);
  }
};

onMounted(() => {
  loadPreferences();
  window.addEventListener('toggle-accessibility-menu', toggleMenu);
  window.addEventListener('keydown', handleGlobalKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('toggle-accessibility-menu', toggleMenu);
  window.removeEventListener('keydown', handleGlobalKeydown);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[99999] flex justify-end bg-n-alpha-black2"
        @mousedown.self="closeMenu"
      >
        <section
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          aria-labelledby="accessibility-dialog-title"
          aria-describedby="accessibility-dialog-description"
          class="flex flex-col w-full h-full max-w-lg border-l shadow-2xl border-n-strong bg-n-background"
          @keydown="handleDialogKeydown"
        >
          <header
            class="flex gap-4 justify-between items-start p-5 border-b border-n-weak"
          >
            <div>
              <h2
                id="accessibility-dialog-title"
                ref="dialogTitleRef"
                tabindex="-1"
                class="text-lg font-semibold text-n-slate-12 focus:outline-none"
              >
                Acessibilidade
              </h2>
              <p
                id="accessibility-dialog-description"
                class="mt-1 text-sm text-n-slate-11"
              >
                Personalize a experiência visual, o teclado e os anúncios do
                leitor de tela.
              </p>
            </div>
            <button
              type="button"
              class="flex justify-center items-center w-9 h-9 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              aria-label="Fechar opções de acessibilidade"
              @click="closeMenu"
            >
              <span aria-hidden="true" class="text-xl">×</span>
            </button>
          </header>

          <div class="overflow-y-auto flex-1 p-5">
            <section aria-labelledby="accessibility-profiles-title">
              <h3
                id="accessibility-profiles-title"
                class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-11"
              >
                Perfis rápidos
              </h3>
              <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  v-for="profile in profiles"
                  :key="profile.id"
                  type="button"
                  class="p-3 text-left rounded-lg border transition-colors"
                  :class="
                    options.profile === profile.id
                      ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                      : 'border-n-weak text-n-slate-11 hover:border-n-strong hover:bg-n-alpha-1'
                  "
                  :aria-pressed="options.profile === profile.id"
                  @click="setProfile(profile.id)"
                >
                  <span class="block text-sm font-medium">{{
                    profile.name
                  }}</span>
                  <span class="block mt-1 text-xs">{{
                    profile.description
                  }}</span>
                </button>
              </div>
            </section>

            <section class="mt-6" aria-labelledby="accessibility-visual-title">
              <h3
                id="accessibility-visual-title"
                class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-11"
              >
                Visão e leitura
              </h3>
              <div
                class="overflow-hidden rounded-lg border divide-y border-n-weak divide-n-weak"
              >
                <div class="flex gap-4 justify-between items-center p-4">
                  <label
                    id="high-contrast-label"
                    for="high-contrast-switch"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Alto contraste
                  </label>
                  <ToggleSwitch
                    id="high-contrast-switch"
                    :model-value="options.highContrast"
                    aria-labelledby="high-contrast-label"
                    @update:model-value="updateToggle('highContrast', $event)"
                  />
                </div>
                <div class="flex gap-4 justify-between items-center p-4">
                  <label
                    id="dark-mode-label"
                    for="dark-mode-switch"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Modo escuro
                  </label>
                  <ToggleSwitch
                    id="dark-mode-switch"
                    :model-value="options.darkMode"
                    aria-labelledby="dark-mode-label"
                    @update:model-value="updateToggle('darkMode', $event)"
                  />
                </div>
                <div class="p-4">
                  <label
                    for="color-vision-select"
                    class="block mb-2 text-sm font-medium text-n-slate-12"
                  >
                    Percepção de cores
                  </label>
                  <select
                    id="color-vision-select"
                    v-model="options.colorVision"
                    class="w-full rounded-lg border border-n-strong bg-n-alpha-1 text-n-slate-12"
                    @change="markAsCustom"
                  >
                    <option
                      v-for="item in colorVisionOptions"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </option>
                  </select>
                </div>
                <div class="flex gap-4 justify-between items-center p-4">
                  <span
                    id="text-scale-label"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Tamanho do texto
                  </span>
                  <div
                    class="flex gap-2 items-center"
                    role="group"
                    aria-labelledby="text-scale-label"
                  >
                    <button
                      type="button"
                      class="w-9 h-9 rounded-lg border border-n-strong text-n-slate-12 disabled:opacity-40"
                      aria-label="Diminuir tamanho do texto"
                      :disabled="options.textScale === 0"
                      @click="changeTextScale(-1)"
                    >
                      A−
                    </button>
                    <output
                      class="min-w-12 text-sm font-semibold text-center text-n-slate-12"
                      aria-live="polite"
                    >
                      {{ textScaleLabel }}
                    </output>
                    <button
                      type="button"
                      class="w-9 h-9 rounded-lg border border-n-strong text-n-slate-12 disabled:opacity-40"
                      aria-label="Aumentar tamanho do texto"
                      :disabled="options.textScale === 5"
                      @click="changeTextScale(1)"
                    >
                      A+
                    </button>
                  </div>
                </div>
                <div class="flex gap-4 justify-between items-center p-4">
                  <label
                    id="reading-font-label"
                    for="reading-font-switch"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Fonte de leitura facilitada
                  </label>
                  <ToggleSwitch
                    id="reading-font-switch"
                    :model-value="options.readingFont"
                    aria-labelledby="reading-font-label"
                    @update:model-value="updateToggle('readingFont', $event)"
                  />
                </div>
                <div class="flex gap-4 justify-between items-center p-4">
                  <label
                    id="highlight-links-label"
                    for="highlight-links-switch"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Destacar links
                  </label>
                  <ToggleSwitch
                    id="highlight-links-switch"
                    :model-value="options.highlightLinks"
                    aria-labelledby="highlight-links-label"
                    @update:model-value="updateToggle('highlightLinks', $event)"
                  />
                </div>
                <div class="flex gap-4 justify-between items-center p-4">
                  <label
                    id="pause-animations-label"
                    for="pause-animations-switch"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Reduzir animações
                  </label>
                  <ToggleSwitch
                    id="pause-animations-switch"
                    :model-value="options.pauseAnimations"
                    aria-labelledby="pause-animations-label"
                    @update:model-value="
                      updateToggle('pauseAnimations', $event)
                    "
                  />
                </div>
              </div>
            </section>

            <section
              class="mt-6"
              aria-labelledby="accessibility-navigation-title"
            >
              <h3
                id="accessibility-navigation-title"
                class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-11"
              >
                Navegação assistida
              </h3>
              <div
                class="overflow-hidden rounded-lg border divide-y border-n-weak divide-n-weak"
              >
                <div class="flex gap-4 justify-between items-center p-4">
                  <label
                    id="enhanced-focus-label"
                    for="enhanced-focus-switch"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Foco de teclado reforçado
                  </label>
                  <ToggleSwitch
                    id="enhanced-focus-switch"
                    :model-value="options.enhancedFocus"
                    aria-labelledby="enhanced-focus-label"
                    @update:model-value="updateToggle('enhancedFocus', $event)"
                  />
                </div>
                <div class="flex gap-4 justify-between items-center p-4">
                  <label
                    id="keyboard-navigation-label"
                    for="keyboard-navigation-switch"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Operação prioritária por teclado
                  </label>
                  <ToggleSwitch
                    id="keyboard-navigation-switch"
                    :model-value="options.keyboardNavigation"
                    aria-labelledby="keyboard-navigation-label"
                    @update:model-value="
                      updateToggle('keyboardNavigation', $event)
                    "
                  />
                </div>
                <div class="flex gap-4 justify-between items-center p-4">
                  <label
                    id="screen-reader-label"
                    for="screen-reader-switch"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Anúncios para leitor de tela
                  </label>
                  <ToggleSwitch
                    id="screen-reader-switch"
                    :model-value="options.screenReaderMode"
                    aria-labelledby="screen-reader-label"
                    @update:model-value="
                      updateToggle('screenReaderMode', $event)
                    "
                  />
                </div>
                <div v-if="options.screenReaderMode" class="p-4">
                  <label
                    for="announcement-verbosity-select"
                    class="block mb-2 text-sm font-medium text-n-slate-12"
                  >
                    Detalhes dos anúncios
                  </label>
                  <select
                    id="announcement-verbosity-select"
                    v-model="options.announcementVerbosity"
                    class="w-full rounded-lg border border-n-strong bg-n-alpha-1 text-n-slate-12"
                    @change="markAsCustom"
                  >
                    <option value="essential">Essenciais</option>
                    <option value="detailed">Detalhados</option>
                  </select>
                </div>
                <div class="flex gap-4 justify-between items-center p-4">
                  <label
                    id="briefing-label"
                    for="briefing-switch"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Briefing ao abrir conversa
                  </label>
                  <ToggleSwitch
                    id="briefing-switch"
                    :model-value="options.conversationBriefing"
                    aria-labelledby="briefing-label"
                    @update:model-value="
                      updateToggle('conversationBriefing', $event)
                    "
                  />
                </div>
                <div class="flex gap-4 justify-between items-center p-4">
                  <label
                    id="sla-announcement-label"
                    for="sla-announcement-switch"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Anunciar alertas de SLA
                  </label>
                  <ToggleSwitch
                    id="sla-announcement-switch"
                    :model-value="options.announceSla"
                    aria-labelledby="sla-announcement-label"
                    @update:model-value="updateToggle('announceSla', $event)"
                  />
                </div>
                <button
                  type="button"
                  class="flex justify-between items-center p-4 w-full text-sm font-medium text-left text-n-slate-12 hover:bg-n-alpha-1"
                  @click="openKeyboardShortcuts"
                >
                  <span>Consultar atalhos de teclado</span>
                  <kbd class="text-xs text-n-slate-11">Ctrl + /</kbd>
                </button>
              </div>
            </section>

            <p class="sr-only" role="status" aria-live="polite">
              {{ statusMessage }}
            </p>

            <div class="pt-5 mt-6 border-t border-n-weak">
              <button
                type="button"
                class="p-3 w-full text-sm font-semibold rounded-lg border border-n-strong text-n-slate-12 hover:bg-n-alpha-1"
                @click="resetOptions"
              >
                Redefinir preferências
              </button>
              <p class="mt-2 text-xs text-center text-n-slate-10">
                As escolhas são salvas no seu perfil e acompanham você em outros
                dispositivos.
              </p>
            </div>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
